import BoxIcon from "icons/BoxIcon";
import { FC, useEffect, useState } from "react";
import { IOrder } from "types/order";
import { formatDateTime } from "utils/datetime";
import PaymentStatus from "./PaymentStatus";
import { Col, Grid, Row, Steps, StepsProps } from "antd";
import StepItem from "./StepItem";
import { E_ORDER_STATUS, E_PAYMENT_STATUS } from "constants/common";
import { Link } from "react-router-dom";
import {
  DeliveryLink,
  DeliveryProviderLabels,
  E_DELIVERY_PROVIDER,
} from "constants/delivery";

interface ITrackingSection {
  data: IOrder;
  isPickup: boolean;
}
const TrackingSection: FC<ITrackingSection> = ({ data, isPickup }) => {
  const [trackingStep, setTrackingStep] = useState<number>(1);
  const { useBreakpoint } = Grid;
  const screens = useBreakpoint();

  const customDot: StepsProps["progressDot"] = (_, { status, index }) => {
    // Only show icons on desktop/tablet
    if (!screens.md) {
      return null;
    }

    return (
      <StepItem
        isActive={status === "finish"}
        logoUrl={`/step_${index + 1}_${
          status === "finish" ? "white" : "gray"
        }.png`}
      />
    );
  };

  useEffect(() => {
    if (data.status === E_ORDER_STATUS.CONFIRMED) {
      if (data.paymentStatus === E_PAYMENT_STATUS.SUCCESS) {
        setTrackingStep(2);
      }
    }
    if (data.status === E_ORDER_STATUS.COMPLETED) {
      setTrackingStep(3);
    }
  }, []);

  if (data.status === E_ORDER_STATUS.PENDING) {
    return (
      <div className="bg-[#CDE2F6] py-4 text-center text-base md:text-lg font-semibold">
        สินค้าของคุณถูกจัดส่งไปยังระบบแล้ว กรุณารอสักครู่
        กำลังอยู่ระหว่างการรอเจ้าหน้าที่จัดการคำสั่งซื้อ
      </div>
    );
  }

  if (data.status === E_ORDER_STATUS.CANCELLED) {
    return (
      <div className="bg-[#FFE0E0] py-4 text-center text-base md:text-lg font-semibold text-[#E54640]">
        คำสั่งซื้อนี้ถูกยกเลิกแล้ว
      </div>
    );
  }

  return (
    <div className="p-4 md:p-8 lg:p-12">
      <div className="mb-6 md:mb-12">
        <div className="grid grid-cols-1 md:grid-cols-[1fr_auto] gap-4 md:gap-6">
          <div className="grid grid-cols-[auto_1fr] gap-4 items-start md:items-center">
            <BoxIcon style={{ height: 20, width: 24 }} />
            <div className="w-full pr-10">
              <div className="text-base md:text-xl">
                <span className="font-semibold">หมายเลขการสั่งซื้อ</span>{" "}
                {data.id}
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 w-full gap-2 md:gap-4">
                <div className="w-full mb-2 md:mb-0">
                  <span className="font-semibold">วันที่สั่งซื้อ</span>{" "}
                  {formatDateTime(data.orderDate)}
                </div>
                <div className="grid grid-cols-1 md:grid-cols-[auto_auto] w-full md:gap-8 items-start">
                  {!isPickup && (
                    <span className="mb-2 md:mb-0">
                      หมายเลขพัสดุ:{" "}
                      {data.trackingNumber ? (
                        <>
                          <span>{data.trackingNumber}</span>
                          <span className="text-sm">
                            ({DeliveryProviderLabels[data.deliverer]})
                          </span>
                        </>
                      ) : (
                        " - "
                      )}
                    </span>
                  )}
                  {data.deliverer && data.trackingNumber && !isPickup && (
                    <Link
                      to={DeliveryLink[data.deliverer as E_DELIVERY_PROVIDER]}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-primary underline font-semibold justify-self-start md:justify-self-end"
                    >
                      <span>เช็ค Tracking ที่นี่</span>
                    </Link>
                  )}
                </div>
              </div>
            </div>
          </div>
          <div className="justify-self-start md:justify-self-end">
            <PaymentStatus status={data.paymentStatus} />
          </div>
        </div>
      </div>
      <div className="px-2 md:px-12 lg:px-24 relative">
        {screens.md && (
          <div className="text-sm top-0 left-0 relative z-10">
            <div className="absolute top-0 left-0 w-full">
              <Row gutter={[16, 16]}>
                <Col
                  md={12}
                  className={`flex justify-center pl-[64px] ${
                    trackingStep === 1
                      ? data.paymentStatus === E_PAYMENT_STATUS.NOT_PAID
                        ? "text-[#828282]"
                        : data.paymentStatus === E_PAYMENT_STATUS.REJECTED
                        ? "text-[#e54640] text-sm"
                        : "text-[#309760]"
                      : ""
                  }`}
                >
                  {trackingStep === 1 &&
                  data.paymentStatus === E_PAYMENT_STATUS.REJECTED
                    ? "หลักฐานการชำระเงินไม่ถูกต้อง กรุณาลองใหม่อีกครั้ง"
                    : "ระบบกำลังตรวจสอบการชำระเงิน"}
                </Col>

                <Col
                  md={12}
                  className={`flex justify-center pr-[140px] ${
                    data.status == E_ORDER_STATUS.COMPLETED
                      ? "text-[#309760]"
                      : "text-[#828282]"
                  }`}
                >
                  {!isPickup && "พัสดุอยู่ระหว่างการจัดส่ง"}
                </Col>
              </Row>
            </div>
          </div>
        )}
        <Steps
          className={` ${
            !screens.md ? "mobile-tracking-steps" : "tracking-section"
          }`}
          current={trackingStep}
          progressDot={screens.md ? customDot : undefined}
          direction={screens.md ? "horizontal" : "vertical"}
          items={[
            {
              title: screens.md ? "1" : "",
              description: "ได้รับคำสั่งซื้อแล้ว",
              status: trackingStep >= 1 ? "finish" : "wait",
            },
            {
              title: screens.md ? "2" : "",
              description: isPickup
                ? "รอรับสินค้าหน้าร้าน"
                : "รอการจัดส่งพัสดุ",
              status: trackingStep >= 2 ? "finish" : "wait",
            },
            {
              title: screens.md ? "3" : "",
              description: isPickup ? "รับสินค้าเรียบร้อย" : "จัดส่งสำเร็จ",
              status: trackingStep >= 3 ? "finish" : "wait",
            },
          ]}
        />
      </div>
    </div>
  );
};
export default TrackingSection;
